// frontend/src/components/shop/ShopItemCard.js
// Shop V1 — Reusable card component for purchasable offers.
// Handles locked/coming-soon state, dynamic button label, and inline confirmation row.

import React from 'react';
import { Button } from '../ui';
import './ShopItemCard.css';

/**
 * ShopItemCard
 *
 * Renders one item in the horizontal shop lineup.
 * Inline confirmation row is shown when confirmPending===true and Quick Buy is OFF.
 *
 * @param {object}      props
 * @param {string}      props.title            — card title
 * @param {string}      props.description      — card description
 * @param {number|'FREE'|null} props.cost      — null = locked
 * @param {boolean}     props.locked           — show "Coming Soon" badge, no button
 * @param {boolean}     props.comingSoon       — show "Coming Soon" badge
 * @param {string}      [props.imageUrl]       — preview image URL for the card
 * @param {'top'|undefined} [props.imagePlacement] — 'top' renders image in-flow at top (letterboxed); default renders as absolute background
 * @param {Function}    props.onPurchase       — called when buy button clicked
 * @param {boolean}     props.confirmPending   — true when this card awaits confirmation
 * @param {Function}    props.onConfirm        — called when Confirm is clicked
 * @param {Function}    props.onCancelConfirm  — called when Cancel is clicked
 * @param {React.ReactNode} props.children     — slot for PunchCard or other extras
 * @param {boolean}     props.isLoading        — disables button during async ops
 */
function ShopItemCard({
    title,
    description,
    cost,
    locked,
    comingSoon,
    imageUrl,
    imagePlacement,
    onPurchase,
    confirmPending,
    onConfirm,
    onCancelConfirm,
    children,
    isLoading,
}) {
    if (!title) {
        throw new Error('ShopItemCard: title prop is required');
    }

    const isLocked = locked || comingSoon;

    // Determine button label — Fix 10: use diamond SVG instead of ⚡
    const isFree = cost === 'FREE' || cost === 0;

    // Build the cost label node (JSX for icon, string for free)
    const costLabelNode = isFree
        ? 'FREE'
        : typeof cost === 'number'
            ? (
                <>
                    {cost}{' '}
                    <img src="/art/diamond_400_white_bg.svg" alt="Mythril" className="mythril-icon shop-item-cost-icon" />
                </>
            )
            : null;

    // Plain text version for confirm row
    const costLabelText = isFree
        ? 'FREE'
        : typeof cost === 'number'
            ? `${cost} Mythril`
            : null;

    return (
        <div className={`shop-item-card${isLocked ? ' shop-item-card--locked' : ''}`}>
            {/* Fix 1 (this task): content wrapper — flex column with flex:1 so margin-top:auto on actions works.
                Content wrapper is rendered FIRST in DOM so it establishes the stacking base.
                The bg-image is rendered LAST (below) so DOM order never lets it paint over content
                even if z-index resolution is ambiguous across browsers. */}
            <div className="shop-item-card-content">
                {/* Fix 3 (this task): top-placement image — in-flow, letterboxed (Random Card Printer only) */}
                {imageUrl && imagePlacement === 'top' && (
                    <img
                        src={imageUrl}
                        alt=""
                        aria-hidden="true"
                        className="shop-item-card-top-image"
                    />
                )}

                {/* Fix 4 (this task): text backdrop — semi-transparent dark container for title/description */}
                <div className="shop-item-card-text-backdrop">
                    <h3 className="shop-item-card-title">{title}</h3>
                    {description && (
                        <p className="shop-item-card-description">{description}</p>
                    )}
                </div>

                {/* Children slot — e.g. PunchCard tracker */}
                {children && (
                    <div className="shop-item-card-extras">
                        {children}
                    </div>
                )}

                {/* Action area — cost renders only inside the button (no separate cost div).
                    Confirm row SWAPS with button (same DOM position, card height unchanged). */}
                {!isLocked && (
                    <div className="shop-item-card-actions">
                        {confirmPending ? (
                            /* Inline confirmation row — replaces button in-place */
                            <div className="shop-item-card-confirm-row">
                                <span className="shop-item-card-confirm-label">
                                    {isFree ? 'Claim free pull?' : `Spend ${costLabelText}?`}
                                </span>
                                <div className="shop-item-card-confirm-buttons">
                                    <Button
                                        variant="primary"
                                        onClick={onConfirm}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? '...' : 'Confirm'}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        onClick={onCancelConfirm}
                                        disabled={isLoading}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            /* Cost label is the button label — appears exactly once */
                            <Button
                                variant="primary"
                                onClick={onPurchase}
                                disabled={isLoading || isLocked}
                                className={`shop-item-card-btn${isFree ? ' shop-item-card-btn--free' : ''}`}
                            >
                                {isLoading ? '...' : (costLabelNode || 'Buy')}
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Coming Soon badge — rendered after content so it overlays everything */}
            {isLocked && (
                <div className="shop-item-card-coming-soon-badge">
                    Coming Soon
                </div>
            )}

            {/* Background image layer — rendered LAST in DOM so z-index layering is unambiguous.
                position:absolute anchored to bottom; z-index:0 keeps it below content (z-index:1).
                Rendering last means even without z-index, DOM paint order puts it on top of content —
                but the content wrapper's z-index:1 > image's z-index:0 ensures content wins. */}
            {imageUrl && imagePlacement !== 'top' && (
                <img
                    src={imageUrl}
                    alt=""
                    aria-hidden="true"
                    className="shop-item-card-bg-image"
                />
            )}
        </div>
    );
}

export default ShopItemCard;
